import { Inject, Injectable, InjectionToken, Optional, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';
import { Gateway_BASE_URL } from './settings.api.client';
import { CoreService } from './core.service';

/** The Zoho Desk Business Messaging channel behind the support chat, from the channel's embed snippet. */
export interface ZohoImChannel {
  /** Zoho IM domain of the data center, e.g. `https://im.zoho.in`. */
  baseUrl: string;
  /** From the embed snippet: `<baseUrl>/api/v1/public/channel/<channelId>/widget`. */
  channelId: string;
}

/**
 * The support chat channel, i.e. `environment.zohoImChannel`. Wired per-project in `app.config.ts`.
 * An app that does not provide this token has no "Technical support" tab at all; an app whose channel
 * is empty shows the tab with the "unavailable" message.
 */
export const ZohoIm_CHANNEL = new InjectionToken<ZohoImChannel>('ZohoIm_CHANNEL');

/** `ReturnedValue` of `GET settings/Support/GetChatContact`. */
export interface SupportChatContact {
  Enabled: boolean;
  /** "{CompanyName} - {OXOSubDomain}", e.g. "Test Store - 3344". */
  Name?: string | null;
  Email?: string | null;
  Phone?: string | null;
}

/** What `open()` resolved to, so the caller can show the unavailable message. */
export type SupportWidgetOpenResult = 'opened' | 'unavailable';

/** Zoho's `contactInfo`: who is chatting. Zoho sends it with the first message of a chat. */
interface ZohoImContactInfo {
  name?: string;
  email?: string;
  phone?: string;
}

// Zoho's global, typed only as far as we use it. We set the first three; Zoho's script adds the rest.
interface ZohoIm {
  locale?: string;
  widgetProps?: { contactInfo?: ZohoImContactInfo; languages?: string[] };
  renderEFCError?: (error: unknown) => void;
  triggerEvent?: (event: 'openWidget' | 'closeWidget') => void;
  getListenersForEvent?: (event: string) => unknown[];
}

declare global {
  interface Window {
    ZOHOIM?: ZohoIm;
  }
}

const SCRIPT_ELEMENT_ID = 'zohoimchannelscript';

// Portal language -> Zoho widget locale. Zoho accepts only its own codes (not `ar`/`ar_SA`).
const ZOHO_LOCALES: Record<'en' | 'ar', string> = { en: 'en_US', ar: 'ar_EG' };

/**
 * Zoho keeps the visitor's current chat in localStorage as `bm_session_<channelId>_GUEST` (undocumented,
 * from Zoho's widget code). Removing it makes the widget start a new chat; the old one stays in Desk.
 */
const SESSION_KEY_PREFIX = 'bm_session_';

/** A hash of whose chat that is, so the next user or company on a shared shop computer never continues it. */
const OWNER_KEY = 'zoho-im-chat-owner';

/**
 * Zoho fires no event once its widget is up, so readiness is polled: the widget is ready once it
 * listens for `openWidget`. A load that hasn't got there within `READY_TIMEOUT_MS` is dropped, and the
 * next `open()` starts over.
 */
const READY_POLL_MS = 250;
const READY_TIMEOUT_MS = 20_000;

/**
 * Zoho Desk Business Messaging (OMNI-3430): Zoho's floating support chat, opened from the Ask panel's
 * "Technical support" tab. Chats reach Zoho Desk as Instant Messaging conversations from the contact
 * `GetChatContact` returns. There is no Zoho sign-in.
 *
 * Zoho's script is loaded on first use only, and its bubble appears from then on, so the user can
 * close and reopen the chat. Zoho reads `contactInfo` once, when its widget renders, so a loaded
 * widget can't switch contact: logout and company switch both reload the page anyway.
 *
 * The contact request follows `KbChatService`: raw `fetch()` with a bearer token fetched fresh from
 * `OidcSecurityService`, against the same gateway base URL as `SettingsAPIClient`. It also sends the
 * `EncryptedCompanyID` header that `OXOAPIClientBase` adds while visiting a company, like every other
 * portal request.
 */
@Injectable({ providedIn: 'root' })
export class ZohoImService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly settings = inject(CoreService);

  /** Resolves once Zoho's widget is ready; reset if loading fails. */
  private ready?: Promise<void>;

  constructor(
    @Optional() @Inject(ZohoIm_CHANNEL) private readonly channel?: ZohoImChannel,
    @Optional() @Inject(Gateway_BASE_URL) private readonly gatewayBaseUrl?: string,
  ) {}

  /** The app wires the support chat at all (the token is provided, possibly empty). */
  get isWired(): boolean {
    return this.channel !== undefined && this.channel !== null;
  }

  /** A channel is set, so the widget can actually load. */
  get isConfigured(): boolean {
    return !!this.channel?.baseUrl && !!this.channel?.channelId;
  }

  /**
   * The portal reloads the page on a language change, so the language read here holds for the
   * page's lifetime.
   */
  private get language(): 'en' | 'ar' {
    return this.settings.getLanguage() === 'en' ? 'en' : 'ar';
  }

  private get loaded(): boolean {
    return !!window.ZOHOIM?.triggerEvent && !!document.getElementById(SCRIPT_ELEMENT_ID);
  }

  private get contactUrl(): string {
    return `${(this.gatewayBaseUrl ?? '').replace(/\/+$/, '')}/settings/Support/GetChatContact?culture=${this.language}`;
  }

  /**
   * Open Zoho's chat widget. Resolves `'unavailable'` when the channel is not configured, the backend
   * switch is off, the contact or script cannot be loaded, or Zoho's widget doesn't start within
   * `READY_TIMEOUT_MS`.
   *
   * `beforeOpen` runs once Zoho is ready, just before the widget appears, and never when it can't
   * open. The Ask panel uses it to close itself first, so the two never overlap.
   */
  async open(beforeOpen?: () => void): Promise<SupportWidgetOpenResult> {
    if (!this.isConfigured) {
      return 'unavailable';
    }

    try {
      if (!this.ready) {
        const contact = await this.getContact();
        if (!contact.Enabled) {
          return 'unavailable';
        }
        this.ready = this.load(contact).catch((err) => {
          this.ready = undefined;
          throw err;
        });
      }
      await this.ready;
    } catch (err) {
      console.error(err);
      return 'unavailable';
    }

    const zoho = window.ZOHOIM;
    if (!zoho?.triggerEvent) {
      return 'unavailable';
    }

    beforeOpen?.();
    zoho.triggerEvent('openWidget');
    return 'opened';
  }

  /** Hide Zoho's widget (the Ask panel is opening again). No-op if the widget never loaded. */
  close(): void {
    if (!this.loaded) {
      return;
    }
    try {
      window.ZOHOIM?.triggerEvent?.('closeWidget');
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Portal logout or company switch: close the widget if it loaded, and forget the chat saved in this
   * browser even if it didn't, so the next person on a shared shop computer doesn't continue it.
   */
  logout(): void {
    this.close();
    this.forgetChat();
  }

  private async getContact(): Promise<SupportChatContact> {
    const token = await firstValueFrom(this.oidcSecurityService.getAccessToken());
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'Accept-Language': this.settings.getLanguage(),
    };
    const visitingCompanyId = sessionStorage.getItem('v_id');
    if (visitingCompanyId) {
      headers['EncryptedCompanyID'] = visitingCompanyId;
    }

    const res = await fetch(this.contactUrl, { headers });
    if (!res.ok) {
      throw new Error(`GetChatContact request failed (${res.status}).`);
    }

    const body = (await res.json()) as { IsSuccess?: boolean; ReturnedValue?: SupportChatContact | null };
    if (!body?.IsSuccess || !body.ReturnedValue || typeof body.ReturnedValue.Enabled !== 'boolean') {
      throw new Error('GetChatContact returned no usable contact.');
    }

    return body.ReturnedValue;
  }

  /** Inject Zoho's channel script once, for `contact`, and resolve when its widget is ready. */
  private async load(contact: SupportChatContact): Promise<void> {
    const contactInfo = toContactInfo(contact);
    await this.claimChat(contactInfo);

    return new Promise<void>((resolve, reject) => {
      let poll: ReturnType<typeof setInterval> | undefined;
      let timer: ReturnType<typeof setTimeout> | undefined;
      let settled = false;
      const done = (err?: Error) => {
        // Only the first outcome counts: a late error must not tear down a widget that already started.
        if (settled) {
          return;
        }
        settled = true;
        clearInterval(poll);
        clearTimeout(timer);
        if (!err) {
          resolve();
          return;
        }
        // Start the next open() from scratch.
        document.getElementById(SCRIPT_ELEMENT_ID)?.remove();
        delete window.ZOHOIM;
        reject(err);
      };

      // Zoho's channel script adds its own settings to this object and reads these when the widget renders.
      // A single language hides the widget's own language menu, so the chat follows the portal's language.
      const locale = ZOHO_LOCALES[this.language];
      window.ZOHOIM = {
        locale,
        widgetProps: { contactInfo, languages: [locale] },
        renderEFCError: () => done(new Error('Zoho could not load the Business Messaging widget files.')),
      };

      poll = setInterval(() => {
        if (window.ZOHOIM?.getListenersForEvent?.('openWidget')?.length) {
          done();
        }
      }, READY_POLL_MS);
      timer = setTimeout(() => done(new Error('Zoho Business Messaging did not start in time.')), READY_TIMEOUT_MS);

      const { baseUrl, channelId } = this.channel!;
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = SCRIPT_ELEMENT_ID;
      script.defer = true;
      script.src = `${baseUrl.replace(/\/+$/, '')}/api/v1/public/channel/${encodeURIComponent(channelId)}/widget`;
      script.onerror = () => done(new Error('Zoho Business Messaging script failed to load.'));
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    });
  }

  /** Forgets the chat saved in this browser when it belongs to another user or company. */
  private async claimChat(contactInfo: ZohoImContactInfo): Promise<void> {
    try {
      const owner = await sha256(`${contactInfo.email ?? ''}|${contactInfo.name ?? ''}`.toLowerCase());
      if (localStorage.getItem(OWNER_KEY) === owner) {
        return;
      }
      this.forgetChat();
      localStorage.setItem(OWNER_KEY, owner);
    } catch {
      // No hashing or storage: start a new chat rather than risk continuing someone else's.
      this.forgetChat();
    }
  }

  private forgetChat(): void {
    const prefix = `${SESSION_KEY_PREFIX}${this.channel?.channelId}_`;
    try {
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      }
      localStorage.removeItem(OWNER_KEY);
    } catch {
      // Storage is blocked, so Zoho can't keep a chat across page loads either.
    }
  }
}

/** Zoho's `contactInfo` from the backend's contact: trimmed, and without empty fields. */
function toContactInfo(contact: SupportChatContact): ZohoImContactInfo {
  const info: ZohoImContactInfo = {};
  const name = contact.Name?.trim();
  const email = contact.Email?.trim();
  const phone = contact.Phone?.trim();
  if (name) {
    info.name = name;
  }
  if (email) {
    info.email = email;
  }
  if (phone) {
    info.phone = phone;
  }
  return info;
}

async function sha256(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}
